import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useHistory } from "react-router-dom";
import api from "../../api";
import { isIOS } from "../../utils/device";
import IOSShareGuide from "./IOSShareGuide";
import "./Tutorial.scss";

export const TUTORIAL_FLAG = "oc_pending_tutorial";

const STEPS = [
  {
    icon: "🛍️",
    title: "One cart for every store",
    body:
      "Save what you're eyeing from any shop on the web into a single cart, so you can compare it all side by side instead of across a dozen tabs.",
  },
  {
    icon: "🧩",
    title: "Add items as you browse",
    body:
      "Install the OllaCart browser extension, then click it on any product page to save that item straight to your cart.",
    action: { label: "Get the extension", href: "https://chromewebstore.google.com/search/ollacart", external: true },
  },
  {
    icon: "📂",
    title: "Group them into collections",
    body:
      "Sort saved items into collections — a trip, a room, a gift list — so the things you're deciding between stay together.",
  },
  {
    icon: "✨",
    title: "Let the assistant find things",
    body:
      "Describe what you want, or upload a photo of an outfit, and the shopping assistant will search real stores and rank the options for you.",
    action: { label: "Try the assistant", to: "/assistant" },
  },
  {
    icon: "💌",
    title: "Share your cart",
    body:
      "Send your cart to friends for a second opinion, or share it as a wishlist so people know exactly what you want.",
  },
];

// On iPhone there's no browser extension — items are saved by sharing them to
// the OllaCart app, so that step teaches the Share menu instead.
const IOS_ADD_STEP = {
  icon: "📲",
  title: "Save from any app with Share",
  body:
    "On iPhone, tap Share on any product — in Safari or a shopping app — and pick OllaCart. Add it to your Share menu once and it's always one tap away.",
  action: { label: "Show me how", guide: true },
};
const STEPS_FOR_DEVICE = isIOS()
  ? STEPS.map((s) => (s.title === "Add items as you browse" ? IOS_ADD_STEP : s))
  : STEPS;

const Tutorial = ({ open, onClose }) => {
  const [step, setStep] = useState(0);
  const [guideOpen, setGuideOpen] = useState(false);
  const history = useHistory();
  const touchStartX = useRef(null);
  const dialogRef = useRef(null);

  const isLast = step === STEPS_FOR_DEVICE.length - 1;

  const finish = useCallback(() => {
    localStorage.removeItem(TUTORIAL_FLAG);
    // Fire-and-forget: a failed write only means we can't tell later that
    // they saw it, which shouldn't block dismissing the tutorial.
    api.completeTutorial().catch(() => {});
    onClose();
  }, [onClose]);

  const next = useCallback(() => {
    setStep((s) => (s >= STEPS_FOR_DEVICE.length - 1 ? s : s + 1));
  }, []);

  const back = useCallback(() => {
    setStep((s) => (s <= 0 ? s : s - 1));
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") finish();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") back();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, finish, next, back]);

  // Lock background scroll while the tutorial is up (matters most on mobile,
  // where the sheet covers the viewport).
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  useEffect(() => {
    if (open && dialogRef.current) dialogRef.current.focus();
  }, [open]);

  if (!open) return null;

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(delta) < 50) return;
    if (delta < 0) next();
    else back();
  };

  const runAction = (action) => {
    if (!action) return;
    if (action.guide) {
      setGuideOpen(true);
      return;
    }
    if (action.external) {
      window.open(action.href, "_blank", "noopener,noreferrer");
      return;
    }
    finish();
    history.push(action.to);
  };

  const current = STEPS_FOR_DEVICE[step];

  const overlay = (
    <div className="oc-tutorial" role="dialog" aria-modal="true" aria-labelledby="oc-tutorial-title">
      <div
        className="oc-tutorial__sheet"
        ref={dialogRef}
        tabIndex={-1}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <button type="button" className="oc-tutorial__skip" onClick={finish}>
          Skip
        </button>

        <div className="oc-tutorial__icon" aria-hidden="true">
          {current.icon}
        </div>

        <h2 className="oc-tutorial__title" id="oc-tutorial-title">
          {current.title}
        </h2>
        <p className="oc-tutorial__body">{current.body}</p>

        {current.action && (
          <button
            type="button"
            className="oc-tutorial__action"
            onClick={() => runAction(current.action)}
          >
            {current.action.label}
          </button>
        )}

        <div className="oc-tutorial__dots" role="tablist" aria-label="Tutorial progress">
          {STEPS_FOR_DEVICE.map((s, i) => (
            <button
              key={s.title}
              type="button"
              className={`oc-tutorial__dot ${i === step ? "is-active" : ""}`}
              onClick={() => setStep(i)}
              aria-label={`Step ${i + 1} of ${STEPS_FOR_DEVICE.length}`}
              aria-selected={i === step}
              role="tab"
            />
          ))}
        </div>

        <div className="oc-tutorial__nav">
          <button
            type="button"
            className="oc-tutorial__btn oc-tutorial__btn--ghost"
            onClick={back}
            disabled={step === 0}
          >
            Back
          </button>
          <button
            type="button"
            className="oc-tutorial__btn oc-tutorial__btn--primary"
            onClick={isLast ? finish : next}
          >
            {isLast ? "Start shopping" : "Next"}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {ReactDOM.createPortal(overlay, document.body)}
      <IOSShareGuide open={guideOpen} onClose={() => setGuideOpen(false)} />
    </>
  );
};

export default Tutorial;
